/**
 * Social Media Domain Menu Provider
 */

import {
  IconArticle, IconBrandFacebook, IconBrandInstagram, IconBrandYoutube,
  IconCurrencyDollar, IconDashboard, IconFileAnalytics, IconPlugConnected,
  IconSettings, IconUsers, IconVideo,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { anyVisibilityCheck, createVisibilityCheck } from './BaseMenuProvider';

class SocialMediaMenuProvider implements DomainMenuProvider {
  domainId = 'social-media';

  getMenuConfig() {
    // Reconciles the two gates that previously disagreed: the domain is declared with
    // requiredRoles ['Admin', 'Social Media Access'], while every menu item was
    // hard-coded to Admin. Permission rules are Admin-passable by default, matching the
    // backend, where each endpoint carries a SocialMedia.* permission.
    const analyticsUsers = anyVisibilityCheck(
      { roles: ['Admin', 'Social Media Access'] },
      { permissions: ['SocialMedia.Dashboard.Read'] },
    );
    const adsUsers = anyVisibilityCheck(
      { roles: ['Admin', 'Social Media Access'] },
      { permissions: ['SocialMedia.Ads.Read'] },
    );
    const adminsOnly = createVisibilityCheck({ roles: ['Admin'] });

    const sections: MenuSection[] = [
      {
        id: 'social-media-overview',
        label: 'Overview',
        items: [
          {
            id: 'social-media-dashboard',
            label: 'Dashboard',
            path: '/app/social-media',
            icon: <IconDashboard size={16} />,
            visible: analyticsUsers,
          },
          {
            id: 'social-media-facebook',
            label: 'Facebook',
            // /platform/:platform, not /facebook — one page serves all three.
            path: '/app/social-media/platform/facebook',
            icon: <IconBrandFacebook size={16} />,
            visible: analyticsUsers,
          },
          {
            id: 'social-media-instagram',
            label: 'Instagram',
            path: '/app/social-media/platform/instagram',
            icon: <IconBrandInstagram size={16} />,
            visible: analyticsUsers,
          },
          {
            id: 'social-media-youtube',
            label: 'YouTube',
            path: '/app/social-media/platform/youtube',
            icon: <IconBrandYoutube size={16} />,
            visible: analyticsUsers,
          },
        ],
      },

      {
        id: 'social-media-content',
        label: 'Content',
        items: [
          {
            id: 'social-media-posts',
            label: 'Posts & Stories',
            path: '/app/social-media/content',
            icon: <IconArticle size={16} />,
            visible: analyticsUsers,
          },
          {
            id: 'social-media-video',
            label: 'Video',
            path: '/app/social-media/video',
            icon: <IconVideo size={16} />,
            visible: analyticsUsers,
          },
        ],
      },

      {
        id: 'social-media-insights',
        label: 'Insights',
        items: [
          {
            id: 'social-media-audience',
            label: 'Audience',
            path: '/app/social-media/audience',
            icon: <IconUsers size={16} />,
            visible: analyticsUsers,
          },
          {
            id: 'social-media-ads',
            label: 'Ads & ROI',
            path: '/app/social-media/ads',
            icon: <IconCurrencyDollar size={16} />,
            visible: adsUsers,
          },
          {
            id: 'social-media-reports',
            label: 'Reports',
            path: '/app/social-media/reports',
            icon: <IconFileAnalytics size={16} />,
            visible: analyticsUsers,
          },
        ],
      },

      {
        id: 'social-media-administration',
        label: 'Administration',
        visible: adminsOnly,
        items: [
          {
            id: 'social-media-admin-accounts',
            label: 'Accounts',
            path: '/app/social-media/admin/accounts',
            icon: <IconPlugConnected size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'social-media-admin-thresholds',
            label: 'Alert Thresholds',
            path: '/app/social-media/admin/thresholds',
            icon: <IconSettings size={16} />,
            visible: adminsOnly,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '2.0.0',
        lastUpdated: Date.now(),
      },
    };
  }
}

export const socialMediaMenuProvider = new SocialMediaMenuProvider();
